import React, { useState, useEffect } from "react";
import { useAuth } from '../context/AuthContext';
import './ProductPage.css';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function ProductPage() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const REACT_APP_API_URL = process.env.REACT_APP_API_BASE_URL;
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newProduct, setNewProduct] = useState({
    productId: "",
    name: "",
    size: "",
    price: "",
    cost: "",
    currentTaxAvailable: "",
    type: "",
  });

  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [templates, setTemplates] = useState([]);
  const [templateFields, setTemplateFields] = useState([]);

  const [selectedProduct, setSelectedProduct] = useState(null);
  

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        if (!token) return;
        const response = await fetch(`${REACT_APP_API_URL}/product-templates`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!response.ok) throw new Error("Failed to fetch templates");
        const data = await response.json();
        setTemplates(data);
      } catch (err) {
        console.error("Error loading templates:", err);
        alert("Error loading templates: " + err.message);
      }
    };

    fetchTemplates();
  }, [token]);

  useEffect(() => {
    if (!selectedTemplate) {
      setTemplateFields([]);
      return;
    }

    const template = templates.find(t => t.templateId === selectedTemplate);
    if (template) {
      // Load the template fields
      setTemplateFields(template.customFields || []);

      const defaults = {};
      template.customFields?.forEach(field => {
        defaults[field.fieldName] = field.defaultValue || "";
      });

      // Reset product data and set template category
      setNewProduct(prev => ({
        ...prev,
        type: template.templateName,
        name: "",
        size: "",
        price: "",
        cost: "",
        currentTaxAvailable: "",
        type: template.templateName,
        ...defaults
      }));
    }
  }, [selectedTemplate, templates]);

  const [kpis, setKpis] = useState({
    totalProducts: 0,
    totalSKUs: 0,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  const categories = Array.from(
    new Set(products.map((p) => p.category || p.type).filter(Boolean))
  );

  const filteredProducts = products.filter((p) => {
      const matchesSearch =
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.sku && p.sku.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesCategory =
        !selectedCategory || (p.category || p.type) === selectedCategory;
      return matchesSearch && matchesCategory;
  });

  // -----------------------------
  // Fetch Products
  // -----------------------------
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);

        // Load cached products if they exist
        const cached = sessionStorage.getItem("products");
        if (cached) {
          setProducts(JSON.parse(cached));
          return;
        }

        if (!token) return;
        const response = await fetch(`${REACT_APP_API_URL}/products`,
          {
            headers: {"Authorization": `Bearer ${token}`}
          }
        );
        if (!response.ok) throw new Error("Failed to fetch products");

        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          throw new Error("API did not return JSON");
        }

        const data = await response.json();
        setProducts(data);

        // Update sessionStorage cache
        sessionStorage.setItem("products", JSON.stringify(data));
      } catch (err) {
        console.error("Error loading products:", err);
        alert("Error loading products: " + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // -----------------------------
  // Back to Dashboard Function
  // -----------------------------
  const handleBackToDashboard = () => {
    window.location.href = "/dashboard";
  };

  // -----------------------------
  // KPI Calculation
  // -----------------------------
  useEffect(() => {
    const totalProducts = products.length;
    const totalSKUs = new Set(products.map((p) => p.sku || p.productId)).size;
    setKpis({ totalProducts, totalSKUs });
  }, [products]);

  // -----------------------------
  // Input Handler
  // -----------------------------
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewProduct((prev) => ({ ...prev, [name]: value }));
  };

  // -----------------------------
  // Add Product
  // -----------------------------
  const handleAddProduct = async (e) => {
    e.preventDefault();

    if (!token) {
      alert("Token not ready yet, please wait a second.");
      return;
    }

    if (!newProduct.name || !newProduct.productId) {
      alert("Please fill in Product ID and Name");
      return;
    }

    const productAttributeValues = templateFields.map(field => ({
      fieldId: field.fieldId,   
      fieldName: field.fieldName,    
      value: newProduct[field.fieldName] || ""
    }));


    try {
      setLoading(true);
      const response = await fetch(`${REACT_APP_API_URL}/products`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
         },
        body: JSON.stringify({
          name: newProduct.name,
          size: newProduct.size,
          costPrice: parseFloat(newProduct.cost) || 0,
          sellingPrice: parseFloat(newProduct.price) || 0,
          tax: parseFloat(newProduct.currentTaxAvailable) || 0,
          type: newProduct.type,
          category: newProduct.type,
          sku: newProduct.productId,

          templateId: selectedTemplate,
          productAttributeValues
        }),
      });

      if (!response.ok) throw new Error("Failed to add product");

      const savedProduct = await response.json();

      const fullProduct = {
        ...savedProduct,
        templateId: selectedTemplate,
        customFields: productAttributeValues
      };

      setProducts(prev => {
        const updated = [...prev, fullProduct];
        sessionStorage.setItem("products", JSON.stringify(updated));
        return updated;
      });

      setNewProduct({
        productId: "",
        name: "",
        size: "",
        price: "",
        cost: "",
        currentTaxAvailable: "",
        type: "",
      });
      setShowForm(false);
      alert("✅ Product added successfully!");
    } catch (err) {
      console.error(err.message);
      alert("❌ Error adding product: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // Delete Product
  // -----------------------------
  const handleDelete = async (product) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;

    try {
      setLoading(true);
      const idToDelete = product.id; // use backend's UUID field
      if(!token) return;
      const response = await fetch(`${REACT_APP_API_URL}/Products/${idToDelete}`, {
        method: "DELETE",
        headers: {"Authorization": `Bearer ${token}`}
      });

      if (!response.ok) throw new Error("Failed to delete product");

      setProducts(prev => {
        const updated = prev.filter(p => p.id !== idToDelete);
        sessionStorage.setItem("products", JSON.stringify(updated));
        return updated;
      });

      alert("✅ Product deleted successfully!");
    } catch (err) {
      console.error("Error deleting product:", err);
      alert("❌ " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // Bulk Upload CSV
  // -----------------------------
  /*const handleBulkUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const rows = text.split("\n").slice(1);
      const newProducts = rows
        .map((row) => {
          const [productId, name, size, price, cost, currentTaxAvailable, type] = row.split(",");
          return { productId, name, size, price, cost, currentTaxAvailable, type };
        })
        .filter((p) => p.name && p.productId);

      setProducts((prev) => [...prev, ...newProducts]);
    };
    reader.readAsText(file);
  };*/

  // -----------------------------
  // JSX
  // -----------------------------

  

  const renderField = (field) => {
    const value = newProduct[field.fieldName] || "";

    switch (field.fieldType) {
      case "text":
        return (
          <input
            type="text"
            name={field.fieldName}
            placeholder=" "
            value={value}
            onChange={handleInputChange}
          />
        );

      case "number":
        return (
          <input
            type="number"
            name={field.fieldName}
            placeholder=" "
            value={value}
            onChange={handleInputChange}
          />
        );

      case "boolean":
        return (
          <input
            type="checkbox"
            name={field.fieldName}
            checked={!!value}
            onChange={(e) =>
              setNewProduct((p) => ({
                ...p,
                [field.fieldName]: e.target.checked,
              }))
            }
          />
        );

      case "dropdown":
        return (
          <select
            name={field.fieldName}
            value={value}
            onChange={handleInputChange}
          >
            <option value="">Select...</option>
            {field.options?.map((opt, idx) => (
              <option key={idx} value={opt.optionValue}>
                {opt.optionValue}
              </option>
            ))}
          </select>
        );

      case "date":
        return (
          <input
            type="date"
            name={field.fieldName}
            placeholder=" "
            value={value}
            onChange={handleInputChange}
          />
        );

      default:
        return null;
    }
  };

  
  
  return (
    <div style={{ padding: "0", fontFamily: "Arial" }}>
      {/* Header with user info and logout */}
      <div className="info-header">
        <div>
          <h3 style={{ color: '#2d5016', margin: 0 }}>
            {user?.fullName || `${user?.firstName} ${user?.lastName}` || user?.email}
          </h3>
          <h3 style={{ color: '#2d5016', margin: 0 }}>{user?.companyName}</h3>
          <p style={{ color: '#666', fontSize: '14px', margin: '4px 0 0 0' }}>
            {user?.email} - {user?.role}
          </p>
        </div>
        <button 
          onClick={logout}
          style={{
            background: '#ff8c00',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          Logout
        </button>
      </div>

      <div style={{ padding: "30px" }}>
        {/* Page Header */}
        <div style={headerStyle}>
          <h1 style={{ margin: 0 }}>Product Inventory</h1>
          <button onClick={handleBackToDashboard} style={backButtonStyle}>
            <ArrowLeft style={{ width: '16px', height: '16px' }} />
            Back to Dashboard
          </button>
        </div>

        {/* KPIs */}
        <div style={kpiContainer}>
          <KPI title="Total Products" value={kpis.totalProducts} color="#22331D" />
          <KPI title="Unique SKUs" value={kpis.totalSKUs} color="#ff9800" />
        </div>

        {/* Actions */}
        <div style={{ marginBottom: "20px" }}>
          <button onClick={() => setShowForm(!showForm)} style={buttonPrimary}>
            + Add New Product
          </button>
          <button onClick={() => navigate('/templates')} style={buttonPrimary}>
            Manage Product templates
          </button>
          {/*
          <label style={buttonSecondary}>
            📤 Bulk Upload
            <input type="file" accept=".csv" onChange={handleBulkUpload} style={{ display: "none" }} />
          </label>*/}
        </div>

        {/* Add Product Form */}
        {showForm && (
          <div className="modal-overlay">
            <div className="product-modal">
              <h3>Add Product</h3>
              <form className="product-modal-form" onSubmit={handleAddProduct}>
                <div className="form-group">
                  <select
                    name="template"
                    value={selectedTemplate}
                    onChange={(e) => setSelectedTemplate(e.target.value)}
                    className="floating-select"
                  >
                    <option value="">Select a template</option>
                    {templates.map(template => (
                      <option key={template.templateId} value={template.templateId}>
                        {template.templateName}
                      </option>
                    ))}
                  </select>
                  <label>Product Template</label>
                </div>

                {templateFields.length > 0 && (
                  <>
                    {templateFields.map((field, index) => (
                      <div className="form-group" key={index}>
                        {renderField(field)}
                        <label>{field.fieldName}</label>
                      </div>
                    ))}
                  </>
                )}

                <div className="form-group">
                  <input
                    type="text"
                    name="productId"
                    value={newProduct.productId}
                    onChange={handleInputChange}
                    required
                    placeholder=" "
                  />
                  <label>Product SKU</label>
                </div>

                <div className="form-group">
                  <input
                    type="text"
                    name="name"
                    value={newProduct.name}
                    onChange={handleInputChange}
                    required
                    placeholder=" "
                  />
                  <label>Product Name</label>
                </div>

                <div className="form-group">
                  <input
                    type="text"
                    name="size"
                    value={newProduct.size}
                    onChange={handleInputChange}
                    placeholder=" "
                  />
                  <label>Size</label>
                </div>

                <div className="form-group">
                  <input
                    type="number"
                    name="price"
                    value={newProduct.price}
                    onChange={handleInputChange}
                    placeholder=" "
                  />
                  <label>Price</label>
                </div>

                <div className="form-group">
                  <input
                    type="number"
                    name="cost"
                    value={newProduct.cost}
                    onChange={handleInputChange}
                    placeholder=" "
                  />
                  <label>Cost</label>
                </div>

                <div className="form-group">
                  <input
                    type="text"
                    name="currentTaxAvailable"
                    value={newProduct.currentTaxAvailable}
                    onChange={handleInputChange}
                    placeholder=" "
                  />
                  <label>Tax</label>
                </div>

                <div className="form-group">
                  <input
                    type="text"
                    name="type"
                    value={newProduct.type}
                    onChange={handleInputChange}
                    placeholder=" "
                  />
                  <label>Category / Type</label>
                </div>

                <div className="modal-actions">
                  <button type="submit" disabled={loading}>
                    {loading ? "Saving..." : "Add Product"}
                  </button>
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={() => setShowForm(false)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Search input */}
        {/* Filters */}
      <div style={{ display: "flex", gap: "10px", marginBottom: "16px" }}>
        <input
          type="text"
          placeholder="Search products..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="product-search"
        />

        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="category-dropdown"
        >
          <option value="">All Categories</option>
          {categories.map((cat, index) => (
            <option key={index} value={cat}>
              {cat}
            </option>
          ))}
        </select>
      </div>

        {/* Product Table */}
        <div className="product-list">
          {filteredProducts.length === 0 ? (
            <p className="no-products">No products found.</p>
          ) : (
            filteredProducts.map((p, index) => (
              <div key={index} className="product-card">
              <div className="product-content">
          
                {/* Text info */}
                <div className="product-info">
                  <h4 className="product-name">{p.name}</h4>
                  <p><strong>SKU:</strong> {p.sku || "—"}</p>
                  <p><strong>Size:</strong> {p.size || "—"}</p>
                  <p><strong>Cost:</strong> R{p.costPrice || 0}</p>
                  <p><strong>Price:</strong> R{p.sellingPrice || 0}</p>
                  <p><strong>Tax:</strong> {p.tax || 0}</p>
                  <p><strong>Category:</strong> {p.category || p.type || "—"}</p>
                  <button
                    className="more-btn"
                    onClick={() => setSelectedProduct(p)}
                  >
                    more...
                  </button>
                </div>
                 
              </div>

              <div className="product-actions">
                {/*<button
                  //onClick={() => handleEdit(p)}
                  className="edit-btn"
                  disabled={loading}
                >
                  Edit
                </button>*/}

                <button
                  onClick={() => handleDelete(p)}
                  className="delete-btn"
                  disabled={loading}
                >
                  Delete
                </button>
              </div>
            </div>

            ))
          )}
        </div>

        {/* more info modal*/}
        {selectedProduct && (
          <div
            className="modal-backdrop"
            onClick={() => setSelectedProduct(null)}
          >
            <div
              className="modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <h3>{selectedProduct.name}</h3>

              <div className="modal-columns">
                {/* Standard fields */}
                <p><strong>SKU:</strong> {selectedProduct.sku || "—"}</p>
                <p><strong>Size:</strong> {selectedProduct.size || "—"}</p>
                <p><strong>Cost:</strong> R{selectedProduct.costPrice || 0}</p>
                <p><strong>Price:</strong> R{selectedProduct.sellingPrice || 0}</p>
                <p><strong>Tax:</strong> {selectedProduct.tax || 0}</p>
                <p><strong>Category:</strong> {selectedProduct.category || selectedProduct.type || "—"}</p>

                {/* Custom fields */}
                {selectedProduct.customFields?.map((field) => (
                  <p key={field.fieldId}>
                    <strong>{field.fieldName}:</strong> {field.value || "—"}
                  </p>
                ))}
              </div>

              <button
                className="close-btn"
                onClick={() => setSelectedProduct(null)}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// -----------------------------
// Helper Components & Styles
// -----------------------------
const KPI = ({ title, value, color }) => (
  <div style={{ flex: "1", minWidth: "150px", background: color, color: "white", padding: "15px", borderRadius: "5px" }}>
    <h3 style={{ margin: "0", fontSize: "1.2em" }}>{title}</h3>
    <p style={{ fontSize: "1.5em", margin: "5px 0 0" }}>{value}</p>
  </div>
);

const headerStyle = { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" };
const kpiContainer = { display: "flex", gap: "20px", marginBottom: "20px", flexWrap: "wrap" };
const backButtonStyle = { display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', color: 'white', borderRadius: '8px', backgroundColor: '#6c757d', border: 'none', cursor: 'pointer',fontSize: '14px',fontWeight: '500'};
const inputStyle = { display: "block", marginBottom: "10px", padding: "8px", width: "100%", borderRadius: "4px", border: "1px solid #ccc"  };
const buttonPrimary = { padding: "10px 20px", background: "#ff9800", color: "white", border: "none", borderRadius: "5px", marginRight: "10px", cursor: "pointer" };
const buttonSecondary = { padding: "10px 20px", background: "#6c757d", color: "white", borderRadius: "5px", cursor: "pointer" };
const formStyle = { marginBottom: "20px", padding: "20px", border: "1px solid #ddd", borderRadius: "5px", maxWidth: "400px" };
const submitButton = { padding: "10px", background: "#ff9800", color: "white", border: "none", borderRadius: "5px", cursor: "pointer", width: "100%" };
const tableStyle = { width: "100%", borderCollapse: "collapse", textAlign: "left" };
const thStyle = { padding: "10px", backgroundColor: "#6A6D69", color: "#fff", border: "1px solid #ddd"};
const tdStyle = { padding: "10px", borderBottom: "1px solid #eee", border: "1px solid #ddd" };
const deleteBtn = { padding: "6px 12px", background: "#dc3545", color: "white", border: "none", borderRadius: "5px", cursor: "pointer" };